import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Building2, FileText, Upload, CheckCircle, Clock, 
  AlertCircle, ArrowRight, ArrowLeft, Shield, Phone, 
  CreditCard, MapPin, Users, Gift, Mail, Lock, Eye, EyeOff,
  Zap, IndianRupee, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import OTPInput from '@/components/OTPInput';
import TermsModal from '@/components/TermsModal';

const STEPS = [
  { id: 1, name: 'Account', icon: User, description: 'Create your account' },
  { id: 2, name: 'KYC Details', icon: FileText, description: 'Personal & business info' },
  { id: 3, name: 'RERA Upload', icon: Upload, description: 'Upload certificate' },
  { id: 4, name: 'Review', icon: CheckCircle, description: 'Submit for approval' },
];

const STATES = [
  'Andhra Pradesh', 'Karnataka', 'Kerala', 'Maharashtra', 'Tamil Nadu', 
  'Telangana', 'Gujarat', 'Rajasthan', 'Delhi', 'Uttar Pradesh', 
  'West Bengal', 'Haryana', 'Punjab', 'Madhya Pradesh', 'Other'
];

export default function BrokerAuth({ setUser }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Login method: 'email' or 'phone'
  const [loginMethod, setLoginMethod] = useState('email');
  const [loginOtpSent, setLoginOtpSent] = useState(false);
  const [loginMobileOtp, setLoginMobileOtp] = useState('');

  // Signup OTP sub-step
  const [signupSubStep, setSignupSubStep] = useState('form'); // 'form' or 'otp'
  const [emailOtp, setEmailOtp] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);

  // T&C
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Form data
  const [accountData, setAccountData] = useState({
    email: '',
    password: '',
    name: '',
    mobile: ''
  });
  
  const [kycData, setKycData] = useState({
    fullName: '',
    phone: '',
    panNumber: '',
    aadharNumber: '',
    reraNumber: '',
    reraState: '',
    companyName: '',
    officeAddress: '',
    city: '',
    state: '',
    pincode: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankIfsc: '',
    bankName: '',
    referralCode: ''
  });
  
  const [reraFile, setReraFile] = useState(null);
  const [generatedReferralCode, setGeneratedReferralCode] = useState('');

  // Check if already logged in as broker
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (token && user.role === 'broker') {
      checkBrokerStatus(user.id);
    }
  }, []);

  // OTP timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const t = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [otpTimer]);

  const checkBrokerStatus = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/broker/status/${id}`);
      const data = await response.json();
      
      if (data.brokerStatus === 'APPROVED') {
        navigate('/broker', { replace: true });
      } else if (data.brokerStatus === 'PENDING_APPROVAL') {
        setCurrentStep(4);
        setUserId(id);
      } else if (data.brokerStatus === 'PENDING_KYC') {
        setCurrentStep(2);
        setUserId(id);
        setIsLogin(false);
      } else if (data.brokerStatus === 'SUSPENDED' || data.brokerStatus === 'REJECTED') {
        toast.error(`Your broker account is ${data.brokerStatus.toLowerCase()}. Please contact support.`);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } catch (error) {
      console.error('Error checking broker status:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: accountData.email,
          password: accountData.password
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      if (data.user.role !== 'broker') {
        throw new Error('This account is not registered as a broker. Please use the investor portal.');
      }
      
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      
      // Give React time to update state before checking status
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check broker status and redirect accordingly
      await checkBrokerStatus(data.user.id);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!termsAccepted) { toast.error('Please accept the Terms & Conditions'); return; }
    const mobile = accountData.mobile.replace(/\D/g, '');
    if (mobile.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: accountData.name,
          email: accountData.email,
          mobile: mobile,
          password: accountData.password,
          role: 'broker',
          termsAccepted: true
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      toast.success('OTPs sent to your email and mobile!');
      setSignupSubStep('otp');
      setOtpTimer(30);
      if (data._debug) console.log('[DEBUG] OTPs:', data._debug);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySignupOtp = async () => {
    if (emailOtp.length !== 6 || mobileOtp.length !== 6) {
      toast.error('Please enter both 6-digit OTPs'); return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: accountData.email, emailOtp, mobileOtp })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Verification failed');

      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      setUserId(data.user.userId || data.user.id);
      setKycData(prev => ({ ...prev, fullName: accountData.name, phone: accountData.mobile }));
      toast.success('Account created! Please complete your KYC.');
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async (channel) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: accountData.email, channel })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to resend');
      toast.success(`${channel === 'email' ? 'Email' : 'Mobile'} OTP resent!`);
      setOtpTimer(30);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleMobileSendOtp = async () => {
    const mobile = accountData.mobile.replace(/\D/g, '');
    if (mobile.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mobile/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to send OTP');
      setLoginOtpSent(true);
      setOtpTimer(30);
      toast.success('OTP sent!');
      if (data._debug) console.log('[DEBUG] Mobile OTP:', data._debug);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMobileVerifyOtp = async () => {
    const mobile = accountData.mobile.replace(/\D/g, '');
    if (loginMobileOtp.length !== 6) { toast.error('Enter a valid 6-digit OTP'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mobile/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp: loginMobileOtp })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'OTP verification failed');

      if (data.user.role !== 'broker') {
        throw new Error('This account is not registered as a broker.');
      }
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      await checkBrokerStatus(data.user.userId || data.user.id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitKYC = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/broker/kyc/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userId,
          ...kycData
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'KYC submission failed');
      }
      
      setGeneratedReferralCode(data.referralCode);
      toast.success('KYC details saved! Please upload your RERA certificate.');
      setCurrentStep(3);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadRERA = async () => {
    if (!reraFile) {
      toast.error('Please select your RERA certificate');
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('file', reraFile);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/broker/kyc/upload-rera`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }
      
      toast.success('RERA certificate uploaded! Your application is pending approval.');
      setCurrentStep(4);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const validateReferralCode = async (code) => {
    if (!code || code.length < 5) {
      setReferralValid(null);
      setReferrerName('');
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/broker/referral/validate/${code}`);
      const data = await response.json();
      
      setReferralValid(data.valid);
      setReferrerName(data.valid ? data.referrerName : '');
    } catch (error) {
      setReferralValid(false);
    }
  };

  // Step 1: Account Creation / Login
  const renderAccountStep = () => (
    <div className="space-y-6">
      {isLogin ? (
        <>
          {/* Login method tabs */}
          <div className="flex mb-2 border-b border-white/10">
            <button onClick={() => { setLoginMethod('phone'); setLoginOtpSent(false); setLoginMobileOtp(''); }}
              className={`flex-1 pb-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                loginMethod === 'phone' ? 'text-[#4ADE80] border-b-2 border-[#4ADE80]' : 'text-white/50 hover:text-white/80'}`}
              data-testid="broker-tab-phone-login">
              <Phone className="w-4 h-4" /> Mobile Login
            </button>
            <button onClick={() => setLoginMethod('email')}
              className={`flex-1 pb-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                loginMethod === 'email' ? 'text-[#4ADE80] border-b-2 border-[#4ADE80]' : 'text-white/50 hover:text-white/80'}`}
              data-testid="broker-tab-email-login">
              <Mail className="w-4 h-4" /> Email Login
            </button>
          </div>

          {loginMethod === 'email' ? (
            <form onSubmit={handleLogin} className="space-y-6" data-testid="broker-email-login-form">
              <div>
                <Label className="text-white/80 mb-2 block">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input type="email" value={accountData.email}
                    onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
                    required className="bg-white/5 border-white/10 text-white pl-10" placeholder="broker@example.com"
                    data-testid="broker-login-email" />
                </div>
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input type={showPassword ? 'text' : 'password'} value={accountData.password}
                    onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
                    required className="bg-white/5 border-white/10 text-white pl-10 pr-10" placeholder="Enter your password"
                    data-testid="broker-login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#4ADE80] text-black hover:bg-[#3fcf70] py-6"
                data-testid="broker-login-submit">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          ) : (
            <div className="space-y-6" data-testid="broker-phone-login-form">
              {!loginOtpSent ? (
                <>
                  <div>
                    <Label className="text-white/80 mb-2 block">Phone Number</Label>
                    <div className="flex gap-2">
                      <div className="bg-white/5 border border-white/10 rounded-md px-3 flex items-center text-white text-sm shrink-0">+91</div>
                      <Input type="tel" value={accountData.mobile}
                        onChange={(e) => setAccountData({ ...accountData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        placeholder="98765 43210" maxLength={10}
                        className="bg-white/5 border-white/10 text-white font-mono tracking-wider"
                        data-testid="broker-login-phone" />
                    </div>
                  </div>
                  <Button onClick={handleMobileSendOtp}
                    disabled={loading || accountData.mobile.replace(/\D/g, '').length !== 10}
                    className="w-full bg-white/10 hover:bg-white/15 border border-white/10 text-white py-6"
                    data-testid="broker-send-otp-btn">
                    {loading ? 'Sending OTP...' : 'Get OTP'} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-white/80">Enter OTP</Label>
                      <span className="text-xs text-white/50">Sent to +91 {accountData.mobile}</span>
                    </div>
                    <OTPInput value={loginMobileOtp} onChange={setLoginMobileOtp} />
                  </div>
                  <Button onClick={handleMobileVerifyOtp}
                    disabled={loading || loginMobileOtp.length !== 6}
                    className="w-full bg-[#4ADE80] text-black hover:bg-[#3fcf70] py-6"
                    data-testid="broker-verify-login-otp-btn">
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>
                  <div className="text-center">
                    {otpTimer > 0 ? (
                      <span className="text-sm text-white/50">Resend in <span className="text-white font-mono">{otpTimer}s</span></span>
                    ) : (
                      <button onClick={handleMobileSendOtp} className="text-sm text-[#4ADE80] hover:underline">Resend OTP</button>
                    )}
                  </div>
                  <button onClick={() => { setLoginOtpSent(false); setLoginMobileOtp(''); }}
                    className="w-full text-sm text-white/50 hover:text-white">Change phone number</button>
                </>
              )}
            </div>
          )}
        </>
      ) : signupSubStep === 'form' ? (
        <div className="space-y-5" data-testid="broker-signup-form">
          <div>
            <Label className="text-white/80 mb-2 block">Full Name</Label>
            <Input type="text" value={accountData.name}
              onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
              required className="bg-white/5 border-white/10 text-white" placeholder="Rahul Sharma"
              data-testid="broker-signup-name" />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block">Email</Label>
            <Input type="email" value={accountData.email}
              onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
              required className="bg-white/5 border-white/10 text-white" placeholder="broker@example.com"
              data-testid="broker-signup-email" />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block">Mobile Number</Label>
            <div className="flex gap-2">
              <div className="bg-white/5 border border-white/10 rounded-md px-3 flex items-center text-white text-sm shrink-0">+91</div>
              <Input type="tel" value={accountData.mobile}
                onChange={(e) => setAccountData({ ...accountData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                placeholder="98765 43210" maxLength={10}
                className="bg-white/5 border-white/10 text-white font-mono tracking-wider"
                data-testid="broker-signup-mobile" />
            </div>
          </div>
          <div>
            <Label className="text-white/80 mb-2 block">Password</Label>
            <div className="relative">
              <Input type={showPassword ? 'text' : 'password'} value={accountData.password}
                onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
                required className="bg-white/5 border-white/10 text-white pr-10"
                placeholder="Min 8 chars, 1 upper, 1 lower, 1 digit"
                data-testid="broker-signup-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {/* T&C */}
          <div className="flex items-start gap-3">
            <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
              className="mt-1 accent-[#4ADE80]" data-testid="broker-terms-checkbox" />
            <p className="text-sm text-white/60">
              I agree to the{' '}
              <button type="button" onClick={() => setTermsOpen(true)} className="text-[#4ADE80] hover:underline" data-testid="broker-terms-link">
                Terms & Conditions
              </button>{' '}and Privacy Policy
            </p>
          </div>
          <Button onClick={handleCreateAccount}
            disabled={loading || !accountData.name || !accountData.email || !accountData.password || !accountData.mobile || !termsAccepted}
            className="w-full bg-[#4ADE80] text-black hover:bg-[#3fcf70] py-6"
            data-testid="broker-signup-submit">
            {loading ? 'Sending OTPs...' : 'Create Account & Continue'}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="space-y-6" data-testid="broker-signup-otp-form">
          <div className="text-center mb-4">
            <h3 className="text-lg text-white mb-1">Verify Your Account</h3>
            <p className="text-sm text-white/50">Enter the OTPs sent to your email and mobile</p>
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">Email OTP</Label>
            <OTPInput value={emailOtp} onChange={setEmailOtp} />
            <div className="flex justify-end mt-2">
              {otpTimer > 0 ? (
                <span className="text-xs text-white/50">Resend in {otpTimer}s</span>
              ) : (
                <button onClick={() => handleResendOtp('email')} className="text-xs text-[#4ADE80] hover:underline"
                  data-testid="broker-resend-email-otp">Resend Email OTP</button>
              )}
            </div>
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">Mobile OTP</Label>
            <OTPInput value={mobileOtp} onChange={setMobileOtp} />
            <div className="flex justify-end mt-2">
              {otpTimer > 0 ? (
                <span className="text-xs text-white/50">Resend in {otpTimer}s</span>
              ) : (
                <button onClick={() => handleResendOtp('mobile')} className="text-xs text-[#4ADE80] hover:underline"
                  data-testid="broker-resend-mobile-otp">Resend Mobile OTP</button>
              )}
            </div>
          </div>
          <Button onClick={handleVerifySignupOtp}
            disabled={loading || emailOtp.length !== 6 || mobileOtp.length !== 6}
            className="w-full bg-[#4ADE80] text-black hover:bg-[#3fcf70] py-6"
            data-testid="broker-verify-signup-btn">
            {loading ? 'Verifying...' : 'Verify & Continue to KYC'}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <button onClick={() => { setSignupSubStep('form'); setEmailOtp(''); setMobileOtp(''); }}
            className="w-full text-sm text-white/50 hover:text-white flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to form
          </button>
        </div>
      )}
      
      <div className="text-center pt-4 border-t border-white/10 space-y-2">
        {isLogin && loginMethod === 'email' && (
          <div>
            <a href="/forgot-password" className="text-sm text-white/60 hover:text-white transition-colors">
              Forgot your password?
            </a>
          </div>
        )}
        <div>
          <button
            onClick={() => { setIsLogin(!isLogin); setSignupSubStep('form'); setLoginOtpSent(false); setLoginMobileOtp(''); setEmailOtp(''); setMobileOtp(''); setTermsAccepted(false); }}
            className="text-sm text-white/60 hover:text-white transition-colors"
            data-testid="broker-toggle-auth-mode"
          >
            {isLogin ? "Don't have an account? Register as Broker" : 'Already registered? Sign in'}
          </button>
        </div>
      </div>

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)}
        onAccept={() => { setTermsAccepted(true); setTermsOpen(false); }} role="broker" />
    </div>
  );

  // Step 2: KYC Details
  const renderKYCStep = () => (
    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
      {/* Personal Details */}
      <div className="space-y-4">
        <h3 className="text-sm text-[#4ADE80] uppercase tracking-wider flex items-center gap-2">
          <User className="w-4 h-4" /> Personal Details
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white/60 text-xs mb-1 block">Full Name</Label>
            <Input
              value={kycData.fullName}
              onChange={(e) => setKycData({ ...kycData, fullName: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm"
              placeholder="As per PAN"
            />
          </div>
          <div>
            <Label className="text-white/60 text-xs mb-1 block">Phone Number</Label>
            <Input
              value={kycData.phone}
              onChange={(e) => setKycData({ ...kycData, phone: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm"
              placeholder="+91 98765 43210"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white/60 text-xs mb-1 block">PAN Number</Label>
            <Input
              value={kycData.panNumber}
              onChange={(e) => setKycData({ ...kycData, panNumber: e.target.value.toUpperCase() })}
              className="bg-white/5 border-white/10 text-white text-sm font-mono"
              placeholder="ABCDE1234F"
              maxLength={10}
            />
          </div>
          <div>
            <Label className="text-white/60 text-xs mb-1 block">Aadhar Number (Optional)</Label>
            <Input
              value={kycData.aadharNumber}
              onChange={(e) => setKycData({ ...kycData, aadharNumber: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm font-mono"
              placeholder="1234 5678 9012"
              maxLength={14}
            />
          </div>
        </div>
      </div>
      
      {/* RERA Details */}
      <div className="space-y-4 pt-4 border-t border-white/10">
        <h3 className="text-sm text-[#4ADE80] uppercase tracking-wider flex items-center gap-2">
          <Building2 className="w-4 h-4" /> RERA Details
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white/60 text-xs mb-1 block">RERA Registration Number</Label>
            <Input
              value={kycData.reraNumber}
              onChange={(e) => setKycData({ ...kycData, reraNumber: e.target.value.toUpperCase() })}
              className="bg-white/5 border-white/10 text-white text-sm font-mono"
              placeholder="RERA/KA/2024/001234"
            />
          </div>
          <div>
            <Label className="text-white/60 text-xs mb-1 block">RERA State</Label>
            <select
              value={kycData.reraState}
              onChange={(e) => setKycData({ ...kycData, reraState: e.target.value })}
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-md"
            >
              <option value="">Select State</option>
              {STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div>
          <Label className="text-white/60 text-xs mb-1 block">Company Name (Optional)</Label>
          <Input
            value={kycData.companyName}
            onChange={(e) => setKycData({ ...kycData, companyName: e.target.value })}
            className="bg-white/5 border-white/10 text-white text-sm"
            placeholder="ABC Realty Pvt Ltd"
          />
        </div>
      </div>
      
      {/* Address */}
      <div className="space-y-4 pt-4 border-t border-white/10">
        <h3 className="text-sm text-[#4ADE80] uppercase tracking-wider flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Office Address
        </h3>
        
        <div>
          <Label className="text-white/60 text-xs mb-1 block">Office Address</Label>
          <Input
            value={kycData.officeAddress}
            onChange={(e) => setKycData({ ...kycData, officeAddress: e.target.value })}
            className="bg-white/5 border-white/10 text-white text-sm"
            placeholder="123, ABC Complex, MG Road"
          />
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-white/60 text-xs mb-1 block">City</Label>
            <Input
              value={kycData.city}
              onChange={(e) => setKycData({ ...kycData, city: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm"
              placeholder="Bangalore"
            />
          </div>
          <div>
            <Label className="text-white/60 text-xs mb-1 block">State</Label>
            <Input
              value={kycData.state}
              onChange={(e) => setKycData({ ...kycData, state: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm"
              placeholder="Karnataka"
            />
          </div>
          <div>
            <Label className="text-white/60 text-xs mb-1 block">Pincode</Label>
            <Input
              value={kycData.pincode}
              onChange={(e) => setKycData({ ...kycData, pincode: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm font-mono"
              placeholder="560001"
              maxLength={6}
            />
          </div>
        </div>
      </div>
      
      {/* Bank Details */}
      <div className="space-y-4 pt-4 border-t border-white/10">
        <h3 className="text-sm text-[#4ADE80] uppercase tracking-wider flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> Bank Details (for Commission Payouts)
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white/60 text-xs mb-1 block">Account Holder Name</Label>
            <Input
              value={kycData.bankAccountName}
              onChange={(e) => setKycData({ ...kycData, bankAccountName: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm"
              placeholder="As per bank records"
            />
          </div>
          <div>
            <Label className="text-white/60 text-xs mb-1 block">Bank Name</Label>
            <Input
              value={kycData.bankName}
              onChange={(e) => setKycData({ ...kycData, bankName: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm"
              placeholder="HDFC Bank"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white/60 text-xs mb-1 block">Account Number</Label>
            <Input
              value={kycData.bankAccountNumber}
              onChange={(e) => setKycData({ ...kycData, bankAccountNumber: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm font-mono"
              placeholder="50200012345678"
            />
          </div>
          <div>
            <Label className="text-white/60 text-xs mb-1 block">IFSC Code</Label>
            <Input
              value={kycData.bankIfsc}
              onChange={(e) => setKycData({ ...kycData, bankIfsc: e.target.value.toUpperCase() })}
              className="bg-white/5 border-white/10 text-white text-sm font-mono"
              placeholder="HDFC0001234"
              maxLength={11}
            />
          </div>
        </div>
      </div>
      
      {/* Referral Code */}
      <div className="space-y-4 pt-4 border-t border-white/10">
        <h3 className="text-sm text-[#4ADE80] uppercase tracking-wider flex items-center gap-2">
          <Gift className="w-4 h-4" /> Referral (Optional)
        </h3>
        
        <div>
          <Label className="text-white/60 text-xs mb-1 block">Were you referred by another broker?</Label>
          <div className="relative">
            <Input
              value={kycData.referralCode}
              onChange={(e) => {
                const code = e.target.value.toUpperCase();
                setKycData({ ...kycData, referralCode: code });
                validateReferralCode(code);
              }}
              className="bg-white/5 border-white/10 text-white text-sm font-mono"
              placeholder="Enter referral code (e.g., RAHUL-A7X9)"
            />
            {referralValid !== null && (
              <div className={`absolute right-3 top-1/2 -translate-y-1/2 ${referralValid ? 'text-[#4ADE80]' : 'text-red-400'}`}>
                {referralValid ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              </div>
            )}
          </div>
          {referralValid && referrerName && (
            <p className="text-xs text-[#4ADE80] mt-1">Referred by: {referrerName}</p>
          )}
        </div>
      </div>
      
      <div className="flex gap-4 pt-4">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(1)}
          className="flex-1 border-white/20 text-white hover:bg-white/10"
        >
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>
        <Button
          onClick={handleSubmitKYC}
          disabled={loading || !kycData.fullName || !kycData.phone || !kycData.panNumber || !kycData.reraNumber || !kycData.reraState}
          className="flex-1 bg-[#4ADE80] text-black hover:bg-[#3fcf70]"
        >
          {loading ? 'Saving...' : 'Save & Continue'}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Step 3: RERA Upload
  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#4ADE80]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Upload className="w-8 h-8 text-[#4ADE80]" />
        </div>
        <h3 className="text-xl text-white mb-2">Upload RERA Certificate</h3>
        <p className="text-white/60 text-sm">Please upload a clear copy of your RERA registration certificate</p>
      </div>
      
      <div 
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          reraFile ? 'border-[#4ADE80] bg-[#4ADE80]/5' : 'border-white/20 hover:border-white/40'
        }`}
      >
        {reraFile ? (
          <div>
            <CheckCircle className="w-12 h-12 text-[#4ADE80] mx-auto mb-4" />
            <p className="text-white font-medium mb-1">{reraFile.name}</p>
            <p className="text-white/40 text-sm">{(reraFile.size / 1024 / 1024).toFixed(2)} MB</p>
            <button 
              onClick={() => setReraFile(null)}
              className="text-red-400 text-sm mt-2 hover:text-red-300"
            >
              Remove & Upload Different File
            </button>
          </div>
        ) : (
          <label className="cursor-pointer block">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setReraFile(e.target.files[0])}
              className="hidden"
            />
            <FileText className="w-12 h-12 text-white/40 mx-auto mb-4" />
            <p className="text-white mb-1">Click to upload or drag and drop</p>
            <p className="text-white/40 text-sm">PDF, JPG, or PNG (max 10MB)</p>
          </label>
        )}
      </div>
      
      {generatedReferralCode && (
        <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-xl p-4">
          <p className="text-sm text-white/60 mb-2">Your Referral Code (share with other brokers):</p>
          <div className="flex items-center gap-2">
            <code className="text-xl font-mono text-[#4ADE80]">{generatedReferralCode}</code>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(generatedReferralCode);
                toast.success('Referral code copied!');
              }}
              className="text-xs text-white/60 hover:text-white px-2 py-1 border border-white/20 rounded"
            >
              Copy
            </button>
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(2)}
          className="flex-1 border-white/20 text-white hover:bg-white/10"
        >
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>
        <Button
          onClick={handleUploadRERA}
          disabled={loading || !reraFile}
          className="flex-1 bg-[#4ADE80] text-black hover:bg-[#3fcf70]"
        >
          {loading ? 'Uploading...' : 'Upload & Submit'}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Step 4: Pending Approval
  const renderPendingStep = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <Clock className="w-10 h-10 text-yellow-500" />
      </div>
      
      <h3 className="text-2xl text-white mb-3">Application Under Review</h3>
      <p className="text-white/60 mb-8 max-w-md mx-auto">
        Thank you for submitting your application. Our team is reviewing your RERA certificate 
        and KYC details. You will receive an email once your account is approved.
      </p>
      
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 max-w-sm mx-auto mb-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-white/60">Status</span>
          <span className="text-yellow-500 font-medium">Pending Approval</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-white/60">Expected Time</span>
          <span className="text-white">24-48 hours</span>
        </div>
      </div>
      
      {generatedReferralCode && (
        <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-xl p-4 max-w-sm mx-auto">
          <p className="text-sm text-white/60 mb-2">Your Referral Code:</p>
          <code className="text-xl font-mono text-[#4ADE80]">{generatedReferralCode}</code>
          <p className="text-xs text-white/40 mt-2">
            Share this with other brokers. When they join and get approved, you will earn rewards!
          </p>
        </div>
      )}
      
      <div className="mt-8">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="border-white/20 text-white hover:bg-white/10"
        >
          Return to Homepage
        </Button>
      </div>
    </div>
  );

  return currentStep === 1 ? (
    /* ═══ STEP 1: Split-panel layout mirroring investor auth ═══ */
    <div className="min-h-screen bg-[#030303] flex">
      {/* Left: Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="w-full max-w-md">
          <div className="mb-10">
            <a href="/"><h1 className="text-2xl font-light tracking-wider text-white">ALT<span className="text-[#4ADE80]">SLICE</span></h1></a>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-full mt-3">
              <Users className="w-3 h-3 text-[#4ADE80]" />
              <span className="text-xs text-[#4ADE80]">Partner Portal</span>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-light text-white mb-2" data-testid="broker-auth-title">
              {isLogin ? 'Welcome back, Partner' :
                (signupSubStep === 'otp' ? 'Verify your account' : 'Become a Partner')}
            </h2>
            <p className="text-gray-500">
              {isLogin ? 'Sign in to access your broker dashboard' :
                (signupSubStep === 'otp' ? 'Enter the OTPs sent to your email and mobile' : 'Start earning on premium real estate deals')}
            </p>
          </div>

          {renderAccountStep()}

          <p className="mt-8 text-xs text-gray-600 text-center">
            By continuing, you agree to our{' '}
            <button onClick={() => setTermsOpen(true)} className="text-gray-400 hover:text-white">Terms of Service</button>
            {' '}and{' '}
            <a href="/privacy" className="text-gray-400 hover:text-white">Privacy Policy</a>
          </p>
        </motion.div>
      </div>

      {/* Right: Broker Benefits */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-[#0A0A0A] to-[#030303] border-l border-white/5 items-center justify-center p-12">
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="max-w-lg">
          <div className="mb-12">
            <h2 className="text-4xl font-light text-white mb-4">
              Grow Your<br /><span className="text-[#4ADE80]">Real Estate Business</span>
            </h2>
            <p className="text-gray-500 text-lg">
              Partner with AltSlice to earn commissions on premium fractional real estate deals. We handle the operations — you focus on your investors.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: Zap, title: 'Zero Paperwork', desc: 'LLP agreements, compliance, legal docs — that\'s our headache. You just close deals.', tag: '100% Digital' },
              { icon: IndianRupee, title: 'Earn Anytime, Anywhere', desc: 'Share curated deals via a simple link. Earn up to 8% of any capital you raise.', tag: 'Up to 8%' },
              { icon: Users, title: 'Fractional Deals', desc: 'Your investors can start from ₹5 Lakhs. No need to find one big investor for high-value properties.', tag: 'From ₹5 Lakhs' },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                  className="group bg-white/5 border border-white/10 hover:border-[#4ADE80]/30 p-5 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#4ADE80]/10 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-[#4ADE80]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-white font-medium">{f.title}</h3>
                        <span className="text-xs bg-[#4ADE80]/20 text-[#4ADE80] px-2 py-0.5">{f.tag}</span>
                      </div>
                      <p className="text-sm text-gray-500">{f.desc}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-[#4ADE80] transition-colors" />
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-3xl font-light text-white mb-1">₹50Cr+</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Capital Raised</p>
            </div>
            <div className="text-center border-x border-white/10">
              <p className="text-3xl font-light text-white mb-1">100+</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Partners</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-light text-white mb-1">8%</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Commission</p>
            </div>
          </div>

          <div className="mt-12 flex items-center justify-center gap-6">
            {['SEBI Compliant', 'RERA Verified', 'Instant Payouts'].map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-500">
                <CheckCircle className="w-4 h-4 text-[#4ADE80]" />
                <span className="text-xs">{t}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  ) : (
    /* ═══ STEPS 2-4: Centered single-column layout ═══ */
    <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-10 px-4">
      <div className="max-w-xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-full mb-4">
            <Users className="w-4 h-4 text-[#4ADE80]" />
            <span className="text-sm text-[#4ADE80]">Partner Portal</span>
          </div>
          <h1 className="text-3xl font-light text-white mb-2">Become an ALTX Partner</h1>
          <p className="text-white/60">Complete your KYC to start earning</p>
        </motion.div>

        {/* Progress Steps */}
        {currentStep < 4 && (
          <div className="flex items-center justify-between mb-8 px-4">
            {STEPS.slice(0, -1).map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                  currentStep > step.id
                    ? 'bg-[#4ADE80] border-[#4ADE80] text-black'
                    : currentStep === step.id
                      ? 'border-[#4ADE80] text-[#4ADE80]'
                      : 'border-white/20 text-white/40'
                }`}>
                  {currentStep > step.id ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                {index < STEPS.length - 2 && (
                  <div className={`w-16 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-[#4ADE80]' : 'bg-white/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
        )}

        <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
          className="bg-white/5 border border-white/10 rounded-2xl p-8">
          {currentStep === 2 && renderKYCStep()}
          {currentStep === 3 && renderUploadStep()}
          {currentStep === 4 && renderPendingStep()}
        </motion.div>

        {currentStep < 4 && (
          <div className="flex items-center justify-center gap-6 mt-8 text-white/40 text-sm">
            <div className="flex items-center gap-2"><Shield className="w-4 h-4" /><span>Secure & Encrypted</span></div>
            <div className="flex items-center gap-2"><FileText className="w-4 h-4" /><span>RERA Compliant</span></div>
          </div>
        )}
      </div>
    </div>
  );
}
